import { utils } from 'pixi.js';

import { Util } from '../core';
import { Ease } from './Ease';
import { LoopType } from './LoopType';
import { TweenUtil } from './TweenUtil';

type Callback = () => void;
type TargetType = { [key: string]: any };
type PropsType = { [key: string]: number };

interface Options {
  speed: number;
  delay: number;
  loopType: LoopType;
  easingFunc: (k: number) => number;
  onComplete?: Callback;
}

const defaultOptions: Options = {
  speed: 1,
  delay: 0,
  loopType: LoopType.CLAMP,
  easingFunc: Ease.linear,
  onComplete: undefined
};

/**
 * State of tween
 */
export enum TweenState {
  Created,
  Playing,
  Paused,
  Completed
}

export class Tween {
  private static tweens: Tween[] = [];

  /**
   * Duration
   */
  public readonly duration: number = 0;

  /**
   * State of tween
   */
  public state: TweenState = TweenState.Created;

  /**
   * Target
   */
  private readonly target: TargetType;

  /**
   * Target properties
   */
  private readonly props: PropsType = {};

  /**
   * Tween options
   */
  private readonly options: Options;

  /**
   * Callbacks on completion of tween
   */
  private readonly onCompleteCallbacks: Callback[] = [];

  /**
   * Elapsed time
   */
  private elapsed: number = 0;

  /**
   * Remaining time of delay
   */
  private remainingDelay: number = 0;

  /**
   * Whether tween is reverse
   */
  private reverse: boolean = false;

  /**
   * Value of properties on start of tween
   */
  private propsStart: PropsType = {};

  public constructor(target: TargetType, props: PropsType, duration: number, options?: Partial<Options>) {
    this.target = target;
    this.props = props;
    this.duration = duration;
    this.options = { ...defaultOptions, ...options };
    this.remainingDelay = this.options.delay;
    if (options && options.onComplete) {
      this.onCompleteCallbacks.push(options.onComplete);
    }
  }

  /**
   * Remove all tweens.
   */
  public static removeAll(): void {
    this.tweens = [];
  }

  /**
   * Update all tweens.
   * @param dt Delta time
   */
  public static updateAll(dt: number): void {
    for (const tween of this.tweens) {
      tween.update(dt);
    }
    const deadTweens = this.tweens.filter(t => t.state === TweenState.Completed);
    for (const deadTween of deadTweens) {
      for (const callback of deadTween.onCompleteCallbacks) {
        callback();
      }
      this.tweens.splice(this.tweens.indexOf(deadTween), 1);
    }
  }

  /**
   * Add tween.
   */
  private static push(tween: Tween): void {
    this.tweens.push(tween);
  }

  /**
   * Progress of tween.
   */
  public progress(): number {
    return this.elapsed / this.duration;
  }

  /**
   * Set callback running after tween completion.
   * @param callback Callback
   */
  public chain(callback: Callback): void {
    this.onCompleteCallbacks.push(callback);
  }

  /**
   * Start tween.
   */
  public start(): void {
    if (this.state !== TweenState.Created) {
      throw new Error('Tween has already started.');
    }
    this.state = TweenState.Playing;
    this.propsStart = {};
    for (const key in this.props) {
      const prop = this.target[key];
      if (prop === undefined) throw new Error('Property is undefined.');
      if (typeof prop !== 'number') throw new Error('Property is not number.');
      this.propsStart[key] = prop;
    }
    Tween.push(this);
  }

  /**
   * Pause tween.
   */
  public pause(): void {
    if (this.state === TweenState.Playing) {
      this.state = TweenState.Paused;
    }
  }

  /**
   * Resume tween.
   */
  public resume(): void {
    if (this.state === TweenState.Paused) {
      this.state = TweenState.Playing;
    }
  }

  /**
   * Destroy tween.
   */
  public stop(): void {
    this.state = TweenState.Completed;
    // remove
    const index = Tween.tweens.indexOf(this);
    if (index >= 0) {
      Tween.tweens.splice(index, 1);
    }
  }

  /**
   * Wait for tween completion.
   */
  public async waitForCompletion(): Promise<void> {
    await Util.waitUntil(() => this.state === TweenState.Completed);
  }

  private update(dt: number): void {
    if (this.state !== TweenState.Playing) return;

    let dt2 = dt;

    // delay
    if (this.remainingDelay > 0) {
      this.remainingDelay -= dt * this.options.speed;
      const rounded = Math.round(this.remainingDelay * 1000) / 1000;
      if (rounded >= 0) return;
      dt2 = -rounded;
      this.remainingDelay = 0;
    }

    // update duration
    this.elapsed += dt2 * this.options.speed * (this.reverse ? -1 : +1);
    this.elapsed = Math.round(this.elapsed * 1000) / 1000;

    const isOver = this.reverse ? this.elapsed <= 0 : this.elapsed >= this.duration;
    if (isOver) {
      // round elapsed time
      this.elapsed = TweenUtil.loopFunc(this.options.loopType, this.elapsed, 0, this.duration);
    }

    // round between 0 to 1
    const elapsedRatio = Math.round((this.elapsed / this.duration) * 1000) / 1000;
    const easedElapsedRatio = this.options.easingFunc(elapsedRatio);

    // update props
    this.updateProps(easedElapsedRatio);

    // after updating props
    if (isOver) {
      if (this.options.loopType === LoopType.CLAMP) {
        // clamp
        this.state = TweenState.Completed;
      } else if (this.options.loopType === LoopType.YOYO) {
        // yoyo
        this.reverse = !this.reverse;
      }
    }
  }

  private updateProps(easedElapsedRatio: number): void {
    for (const key in this.props) {
      const propStart = this.propsStart[key];
      const propEnd = this.props[key];
      if (key === 'tint') {
        // for tint
        const startArray = utils.hex2rgb(propStart);
        const endArray = utils.hex2rgb(propEnd);
        for (let i = 0; i < 3; i++) {
          endArray[i] = startArray[i] + (endArray[i] - startArray[i]) * easedElapsedRatio;
        }
        this.target[key] = utils.rgb2hex(endArray);
      } else {
        this.target[key] = (propEnd - propStart) * easedElapsedRatio + propStart;
      }
    }
  }
}
